import type * as PIXI from 'pixi.js';

import { scaleRadiusPair } from './roundedRectPath';

const MIN_HALF_ANGLE_TANGENT = 1e-6;

const getHalfCornerAngleTangent = (points: readonly number[], index: number): number => {
    const count = points.length >> 1;
    const previous = (index + count - 1) % count;
    const next = (index + 1) % count;
    const x = points[index * 2];
    const y = points[index * 2 + 1];
    const toPreviousX = points[previous * 2] - x;
    const toPreviousY = points[previous * 2 + 1] - y;
    const toNextX = points[next * 2] - x;
    const toNextY = points[next * 2 + 1] - y;
    const cross = toPreviousX * toNextY - toPreviousY * toNextX;
    const dot = toPreviousX * toNextX + toPreviousY * toNextY;
    return Math.tan(Math.atan2(Math.abs(cross), dot) / 2);
};

/**
 * Returns, for each corner of a closed polygon, how far along its two edges the rounding starts.
 * Distances are scaled down, preserving their relative proportions, so that the arcs of the two
 * corners sharing an edge never overlap. Corners that fold back on themselves stay sharp.
 */
const getClampedTangentDistances = (
    points: readonly number[],
    radii: readonly number[],
    halfAngleTangents: readonly number[],
): number[] => {
    const count = points.length >> 1;
    const desiredDistances = halfAngleTangents.map((tangent, i) =>
        tangent < MIN_HALF_ANGLE_TANGENT ? 0 : (radii[i] ?? 0) / tangent,
    );
    const distances = desiredDistances.slice();
    for (let i = 0; i < count; i++) {
        const next = (i + 1) % count;
        const edgeLength = Math.hypot(
            points[next * 2] - points[i * 2],
            points[next * 2 + 1] - points[i * 2 + 1],
        );
        const [fromCorner, toCorner] = scaleRadiusPair(
            [desiredDistances[i], desiredDistances[next]],
            edgeLength,
        );
        distances[i] = Math.min(distances[i], fromCorner);
        distances[next] = Math.min(distances[next], toCorner);
    }
    return distances;
};

/**
 * Traces a closed polygon whose corners are each rounded by their own radius. Radii that don't fit
 * along the corner's edges are reduced. A missing radius leaves its corner sharp.
 *
 * @param graphics the graphics to draw the path into, inside a fill
 * @param points flat [x1, y1, x2, y2, ...] coordinates of the polygon's corners
 * @param radii the radius of each corner, in the same units as points
 */
export const drawRoundedPolygon = (
    graphics: PIXI.Graphics,
    points: readonly number[],
    radii: readonly number[],
) => {
    const count = points.length >> 1;
    const halfAngleTangents = Array.from({ length: count }, (_, i) =>
        getHalfCornerAngleTangent(points, i),
    );
    const tangentDistances = getClampedTangentDistances(points, radii, halfAngleTangents);

    const last = count - 1;
    const lastToFirstLength = Math.hypot(
        points[0] - points[last * 2],
        points[1] - points[last * 2 + 1],
    );
    const firstArcStart = lastToFirstLength === 0 ? 0 : tangentDistances[0] / lastToFirstLength;
    graphics.moveTo(
        points[0] + (points[last * 2] - points[0]) * firstArcStart,
        points[1] + (points[last * 2 + 1] - points[1]) * firstArcStart,
    );
    for (let i = 0; i < count; i++) {
        const next = (i + 1) % count;
        graphics.arcTo(
            points[i * 2],
            points[i * 2 + 1],
            points[next * 2],
            points[next * 2 + 1],
            tangentDistances[i] * halfAngleTangents[i],
        );
    }
    graphics.closePath();
};
